const DEFAULT_CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  GBP: "£",
  EUR: "€",
  AUD: "A$",
  CAD: "C$",
  ZAR: "R",
  NZD: "NZ$",
  BRL: "R$",
};

let _currencySymbols: Record<string, string> = { ...DEFAULT_CURRENCY_SYMBOLS };

export function setCurrencySymbols(symbols: Record<string, string>): void {
  _currencySymbols = { ...symbols };
}

export function getCurrencySymbol(code: string): string {
  const upper = code.toUpperCase();
  return _currencySymbols[upper] || upper + " ";
}

/**
 * Task #2342 — currencies whose default symbol (`$`) is shared with
 * other currencies, so the bare symbol is ambiguous to users. Render
 * these with a trailing ISO code (e.g. `$120 NZD`). Currencies whose
 * symbol is unambiguous (`£`, `€`, etc.) render with the symbol alone.
 */
export const AMBIGUOUS_DOLLAR_CODES: ReadonlySet<string> = new Set([
  "USD", "AUD", "CAD", "NZD", "SGD", "HKD", "MXN", "ARS", "CLP", "COP",
  "TWD", "FJD", "BBD", "BMD", "BSD", "BZD", "JMD", "KYD", "TTD", "XCD",
]);

export function isAmbiguousDollarCode(code: string | null | undefined): boolean {
  return AMBIGUOUS_DOLLAR_CODES.has(String(code || "").toUpperCase());
}

/**
 * Task #2444 — default formatting locale. Callers that don't yet pass
 * an explicit locale fall back here so existing surfaces don't
 * regress. The ambiguous-dollar branch is intentionally locked to
 * `en-GB` regardless of the caller's locale: `$1,200 NZD` must look
 * identical on the Danish market as on the UK market so the renter
 * isn't confused about which "$" they're looking at.
 */
const DEFAULT_FORMAT_LOCALE = "en-GB";

function formatNumber(amount: number, digits: Intl.NumberFormatOptions, locale: string = DEFAULT_FORMAT_LOCALE): string {
  return amount.toLocaleString(locale, {
    ...digits,
  });
}

function intlCurrencyFormat(
  amount: number,
  currencyCode: string,
  locale: string = DEFAULT_FORMAT_LOCALE,
  opts?: { decimals?: number },
): string {
  const code = currencyCode.toUpperCase();
  const hasDecimals = amount % 1 !== 0;
  const decimals = opts?.decimals == null ? null : Math.max(0, Math.floor(opts.decimals));
  const digits = {
    minimumFractionDigits: decimals ?? (hasDecimals ? 2 : 0),
    maximumFractionDigits: decimals ?? 2,
  };
  // Task #2342: ambiguous-symbol codes render as bare `$` + ISO suffix
  // ("$120 NZD") rather than locale-prefixed "NZ$120". Keeps every
  // surface consistent through a single helper. Unambiguous codes
  // (£, €, etc.) keep their locale symbol with no suffix.
  //
  // Task #2444: only the unambiguous Intl path picks up the caller's
  // locale — the ambiguous-dollar numeric portion stays on en-GB so
  // `$1,200 NZD` reads the same in every market.
  if (AMBIGUOUS_DOLLAR_CODES.has(code)) {
    return `$${formatNumber(amount, digits)} ${code}`;
  }
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      ...digits,
    });
    // Only these approved glyphs are locale-independent. Keep Intl's
    // numeric parts, placement and whitespace; other currencies retain
    // their normal identifying symbol/code (not a global narrowSymbol).
    const glyph = code === "GBP" ? "£" : code === "EUR" ? "€" : null;
    if (!glyph) return formatter.format(amount);
    return formatter.formatToParts(amount)
      .map((part) => part.type === "currency" ? glyph : part.value)
      .join("");
  } catch {
    const sym = getCurrencySymbol(code);
    return `${sym}${formatNumber(amount, digits, locale)}`;
  }
}

export function formatPrice(
  amount: number | null | undefined,
  currencyCode: string = "USD",
  locale?: string,
): string | null {
  if (amount == null) return null;
  return intlCurrencyFormat(amount, currencyCode, locale);
}

export function formatCurrency(
  amount: number,
  currencyCode: string = "USD",
  locale?: string,
  opts?: { decimals?: number },
): string {
  return intlCurrencyFormat(amount, currencyCode, locale, opts);
}

/**
 * Task #2342 — Dual-currency disclosure used at checkout when the
 * renter's preferred currency differs from the actual charge currency.
 * Returns the two formatted strings; callers compose them into the
 * surface-appropriate copy (e.g. "Your payment of {preferred} will be
 * charged as {charge}").
 */
export function formatChargeDisclosure(
  preferredAmount: number,
  preferredCurrency: string,
  chargeAmount: number,
  chargeCurrency: string,
  locale?: string,
): { preferred: string; charge: string; sameCurrency: boolean } {
  const sameCurrency =
    String(preferredCurrency || "").toUpperCase() ===
    String(chargeCurrency || "").toUpperCase();
  return {
    preferred: intlCurrencyFormat(preferredAmount, preferredCurrency, locale),
    charge: intlCurrencyFormat(chargeAmount, chargeCurrency, locale),
    sameCurrency,
  };
}
